import React, { forwardRef, useLayoutEffect, useReducer, useRef } from "react";
import getArea from "./getArea";
import parsePhone, { isForeignPhone } from "./parsePhone";
import { formatPhone, normalizePhoneInput } from "./phoneFormat";

interface PhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  isMobile?: boolean;
  /** Lo pasa ContactoTelefonoStep; no es un atributo del input. */
  area?: unknown;
  /**
   * Si está, el tipo se elige solo cuando el número lo dice (549 o 15 es
   * celular, 54 sin 9 es fijo) y se avisa por acá antes del onChange.
   */
  onIsMobileDetected?: (isMobile: boolean) => void;
}

const digitsOf = (text: string) => text.replace(/\D/g, "");

const MOBILE_PREFIX = "15";

/** Un número de otro país va sin máscara, pero solo con dígitos y separadores. */
const NOT_IN_FOREIGN_PHONE = /[^\d\s()+\-.]/g;
/** El máximo de dígitos de un número internacional. */
const FOREIGN_MAX_DIGITS_LENGTH = 15;

/** Ningún número fijo empieza con 15 después del área. */
const hasMobilePrefix = (national: string) => {
  const number = national.startsWith("0") ? national : `0${national}`;
  const area = getArea(number);
  return !!area && number.substring(area.Code.length).startsWith(MOBILE_PREFIX);
};

/** El tipo que dice el propio número, o null si no dice nada. */
const typeOf = (digits: string, isMobile: boolean): boolean | null => {
  if (digits.startsWith("549")) return true;
  // Con 54 sin 9 es fijo, salvo que tenga el 15 después del área.
  if (digits.startsWith("54") && digits.length > 2)
    return (
      hasMobilePrefix(digits.substring(2)) ||
      (parsePhone(digits)?.isMobile ?? false)
    );
  if (isMobile || digits.startsWith("5")) return null;

  return hasMobilePrefix(digits) ? true : null;
};

/** El área exacta, no la más larga que empiece igual. */
const isExactArea = (code: string) => getArea(code)?.Code === code;

/**
 * Dónde empieza el 15 de celular que pone la máscara, si hay número después.
 * Mientras se corrige el área, el 15 está justo antes del número que se guardó.
 */
const mobilePrefixStart = (
  number: string,
  subscriberWhileEditingArea: string | null
): number | null => {
  const subscriber = subscriberWhileEditingArea;
  if (subscriber !== null && number.endsWith(MOBILE_PREFIX + subscriber))
    return number.length - subscriber.length - MOBILE_PREFIX.length;

  const area = getArea(number);
  if (!area) return null;

  const start = area.Code.length;
  return number.substring(start, start + MOBILE_PREFIX.length) ===
    MOBILE_PREFIX && number.length > start + MOBILE_PREFIX.length
    ? start
    : null;
};

/** La posición en el texto que queda justo después de `count` dígitos. */
const positionAfterDigits = (text: string, count: number) => {
  if (count <= 0) return 0;

  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (/\d/.test(text[i])) seen++;
    if (seen === count) return i + 1;
  }
  return text.length;
};

/**
 * Muestra el número con la máscara, (0341) 15 522-7905, y le pasa a onChange
 * solo los dígitos, con 0 y 15. Se escribe el formato a mano en vez de usar
 * react-text-mask: la máscara de antes metía el 0 y el 15 encima de lo que se
 * tipeaba y se comía dígitos.
 */
const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(props, ref) {
    const {
      isMobile = true,
      area,
      value,
      onChange,
      onIsMobileDetected,
      ...other
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const nextCaret = useRef<number | null>(null);
    // Si el número no cambia, el TextField no vuelve a dibujar y el cursor no
    // se acomoda.
    const [, render] = useReducer((count: number) => count + 1, 0);
    // Mientras se corrige el área de un celular, el número que va después del
    // 15 queda fijo. Si no, sus primeros dígitos completan el área que está a
    // medio escribir y el número se corre.
    const subscriberWhileEditingArea = useRef<string | null>(null);

    /** El área a medio escribir, o null si no se está corrigiendo el área. */
    const areaBeingEdited = (digits: string, mobile = isMobile) => {
      const subscriber = subscriberWhileEditingArea.current;
      return mobile &&
        subscriber !== null &&
        digits.endsWith(MOBILE_PREFIX + subscriber)
        ? digits.substring(
            0,
            digits.length - MOBILE_PREFIX.length - subscriber.length
          )
        : null;
    };

    const show = (digits: string, mobile = isMobile) => {
      if (isForeignPhone(digits)) return digits;
      const area = areaBeingEdited(digits, mobile);
      return area === null
        ? formatPhone(digits, mobile)
        : `(${area}) ${MOBILE_PREFIX} ${subscriberWhileEditingArea.current}`;
    };

    const number = String(value ?? "");
    const text = show(number);

    useLayoutEffect(() => {
      if (nextCaret.current === null || !inputRef.current) return;
      inputRef.current.setSelectionRange(nextCaret.current, nextCaret.current);
      nextCaret.current = null;
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const typed = event.target.value;
      const caret = event.target.selectionStart ?? typed.length;
      const caretAtEnd = caret === typed.length;

      if (isForeignPhone(typed)) {
        subscriberWhileEditingArea.current = null;
        const cleaned = typed.replace(NOT_IN_FOREIGN_PHONE, "");
        const tooLong = digitsOf(cleaned).length > FOREIGN_MAX_DIGITS_LENGTH;
        const next = tooLong ? number : cleaned;
        nextCaret.current = tooLong
          ? Math.max(caret - (typed.length - number.length), 0)
          : typed.substring(0, caret).replace(NOT_IN_FOREIGN_PHONE, "").length;
        if (next === number) render();
        onChange?.({
          target: { value: next },
        } as React.ChangeEvent<HTMLInputElement>);
        return;
      }

      const previous = digitsOf(text);
      let digits = digitsOf(typed);
      let digitsBeforeCaret = digitsOf(typed.substring(0, caret)).length;
      const forward =
        (event.nativeEvent as InputEvent | undefined)?.inputType ===
        "deleteContentForward";

      // Se borró solo un separador: se borra también el dígito de al lado
      // (el anterior con backspace, el siguiente con suprimir), si no la
      // máscara lo vuelve a poner y el borrado queda trabado.
      if (
        digits === previous &&
        typed.length < text.length &&
        !isForeignPhone(text)
      ) {
        if (!forward) digitsBeforeCaret = Math.max(digitsBeforeCaret - 1, 0);
        digits =
          digits.substring(0, digitsBeforeCaret) +
          digits.substring(digitsBeforeCaret + 1);
      }

      const prefixStart = isMobile
        ? mobilePrefixStart(previous, subscriberWhileEditingArea.current)
        : null;

      // Borrar el 15 de celular no hace nada: el cursor pasa por encima, como
      // con un separador. Con backspace el cursor queda donde estaba el dígito
      // borrado; con suprimir salta después del 15, si no queda trabado.
      if (
        prefixStart !== null &&
        digits.length === previous.length - 1 &&
        digitsBeforeCaret >= prefixStart &&
        digitsBeforeCaret < prefixStart + MOBILE_PREFIX.length
      ) {
        digits = previous;
        if (forward) digitsBeforeCaret = prefixStart + MOBILE_PREFIX.length;
      }

      // Un dígito tipeado entre el 1 y el 5 del 15, o justo antes del 15 sin
      // cambiar el área, va al principio del número.
      const typedAt = digitsBeforeCaret - 1;
      if (
        prefixStart !== null &&
        digits.length === previous.length + 1 &&
        (typedAt === prefixStart + 1 ||
          (typedAt === prefixStart &&
            subscriberWhileEditingArea.current === null &&
            getArea(digits)?.Code === getArea(previous)?.Code))
      ) {
        const end = prefixStart + MOBILE_PREFIX.length;
        digits =
          previous.substring(0, end) +
          digits[typedAt] +
          previous.substring(end);
        digitsBeforeCaret = end + 1;
        if (subscriberWhileEditingArea.current !== null)
          subscriberWhileEditingArea.current = digits.substring(end);
      }

      // Una edición antes del 15 empieza a corregir el área.
      if (prefixStart !== null && subscriberWhileEditingArea.current === null) {
        const shift = digits.length - previous.length;
        const editAt =
          shift < 0 ? digitsBeforeCaret : digitsBeforeCaret - shift;
        if (editAt < prefixStart)
          subscriberWhileEditingArea.current = previous.substring(
            prefixStart + MOBILE_PREFIX.length
          );
      }

      // El área se busca solo en lo que se está escribiendo, y el número se
      // vuelve a armar recién cuando es un área que existe.
      // Con el área a medio corregir, una edición en el número cambia el número
      // guardado y el área sigue a medio corregir.
      // Si se reemplazó todo el campo (por ejemplo, pegando encima), el texto ya
      // no empieza con el área a medio corregir y se arma de nuevo.
      const lockedArea = areaBeingEdited(previous);
      if (
        lockedArea !== null &&
        areaBeingEdited(digits) === null &&
        typed.startsWith(`(${lockedArea}) ${MOBILE_PREFIX} `)
      )
        subscriberWhileEditingArea.current = digits.substring(
          lockedArea.length + MOBILE_PREFIX.length
        );

      const detected = onIsMobileDetected ? typeOf(digits, isMobile) : null;
      const mobile = detected ?? isMobile;
      if (detected !== null && detected !== isMobile)
        onIsMobileDetected?.(detected);

      const areaTyped = areaBeingEdited(digits, mobile);
      let next: string;
      // Un dígito insertado que cambia el área no recorta el número: si no
      // entra, quedan los dígitos de más y la validación avisa que sobran.
      // Insertar en el número sí recorta la cola, como pegar en el medio.
      const keepAll = (full: string, normalized: string) =>
        !caretAtEnd &&
        digits.length > previous.length &&
        full.startsWith("0") &&
        normalized.length < full.length &&
        getArea(full)?.Code !== getArea(previous)?.Code
          ? full
          : normalized;

      if (areaTyped === null) {
        subscriberWhileEditingArea.current = null;
        next = keepAll(digits, normalizePhoneInput(digits, mobile));
      } else {
        const areaCode = areaTyped.startsWith("0")
          ? areaTyped
          : `0${areaTyped}`;
        if (isExactArea(areaCode)) {
          // Con el área a medio corregir el área cambió seguro: no se compara
          // con la anterior, que puede tomar el 1 del 15 (0347 + 1 es 03471).
          const full =
            areaCode + MOBILE_PREFIX + subscriberWhileEditingArea.current;
          const normalized = normalizePhoneInput(full, isMobile);
          next =
            !caretAtEnd && normalized.length < full.length ? full : normalized;
          subscriberWhileEditingArea.current = null;
        } else next = digits;
      }
      const nextText = show(next, mobile);

      // El 0 y el 15 que se agregan solos quedan antes del cursor. Si el número
      // no cambió, el cursor queda donde estaba.
      const added =
        next === number ? 0 : Math.max(next.length - digits.length, 0);
      nextCaret.current = caretAtEnd
        ? nextText.length
        : positionAfterDigits(nextText, digitsBeforeCaret + added);
      if (next === number) render();

      // Del evento solo se lee target.value: el TextField es controlado.
      onChange?.({
        target: { value: next },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <input
        {...other}
        ref={(element) => {
          inputRef.current = element;
          if (typeof ref === "function") ref(element);
          else if (ref) ref.current = element;
        }}
        value={text}
        onChange={handleChange}
      />
    );
  }
);

export default PhoneInput;
